/**
 * Il modulo pubblico di iscrizione ai corsi: voci dei menu, testi dei
 * consensi, controlli e composizione di lead e cliente.
 *
 * Sta fuori da src/ perche' lo usano sia la pagina (per dire subito a chi
 * compila cosa manca) sia la funzione iscrizione-corsi (che non si fida di
 * quello che arriva dal browser e ricontrolla tutto). Una sola copia delle
 * regole: se cambiassero in un posto solo, la pagina accetterebbe un modulo
 * che il server poi rifiuta.
 */

import { codiceFiscaleValido, dataNascitaCoincide, normalizzaCodiceFiscale } from './codiceFiscale.ts';
import { REGIONI, trovaProvincia } from './regioniProvince.ts';

// ---------------------------------------------------------------------------
// Voci dei menu
// ---------------------------------------------------------------------------

export const TITOLI = [
  'Sig.', 'Sig.ra', 'Dott.', 'Dott.ssa', 'Ing.', 'Arch.', 'Geom.', 'Per. Ind.', 'Avv.', 'Prof.', 'Prof.ssa',
] as const;

export const QUALIFICHE = [
  'Datore di lavoro',
  'Dirigente',
  'Preposto',
  'Lavoratore',
  'RSPP',
  'ASPP',
  'RLS',
  'Addetto antincendio',
  'Addetto primo soccorso',
  'Medico competente',
  'Coordinatore per la sicurezza (CSP/CSE)',
  'Consulente',
  'Formatore',
  'Libero professionista',
  'Studente',
  'Altro',
] as const;

export const ATTIVITA = [
  'Studio di consulenza',
  'Studio tecnico / professionale',
  'Industria / manifattura',
  'Edilizia / costruzioni',
  'Impiantistica / manutenzione',
  'Commercio',
  'Servizi',
  'Trasporti / logistica',
  'Agricoltura',
  'Turismo / ristorazione',
  'Sanità',
  'Scuola / formazione',
  'Pubblica amministrazione',
  'Associazione / ente no profit',
  'Altro',
] as const;

export const DIPENDENTI = ['nessuno', 'meno di 10', 'da 10 a 49', 'da 50 a 249', '250 e oltre'] as const;

/** Ordini e collegi che riconoscono crediti formativi (CFP) sui corsi di sicurezza. */
export const ORDINI_PROFESSIONALI = [
  'Ordine degli Ingegneri',
  'Ordine degli Architetti, Pianificatori, Paesaggisti e Conservatori',
  'Collegio dei Geometri e Geometri Laureati',
  'Ordine dei Periti Industriali e dei Periti Industriali Laureati',
  'Collegio dei Periti Agrari e dei Periti Agrari Laureati',
  'Collegio degli Agrotecnici e degli Agrotecnici Laureati',
  'Ordine dei Dottori Agronomi e dei Dottori Forestali',
  'Ordine dei Chimici e dei Fisici',
  'Ordine dei Geologi',
  'Ordine dei Consulenti del Lavoro',
  'Ordine dei Dottori Commercialisti e degli Esperti Contabili',
  'Altro',
] as const;

export const NAZIONE_ITALIA = 'Italia';

export const NAZIONI = [
  NAZIONE_ITALIA, 'Albania', 'Austria', 'Belgio', 'Francia', 'Germania', 'Grecia', 'Paesi Bassi', 'Polonia',
  'Portogallo', 'Regno Unito', 'Romania', 'San Marino', 'Spagna', 'Svizzera', 'Altro',
] as const;

/**
 * I testi esatti dei consensi. La pagina li mostra, la funzione li salva
 * insieme alle risposte: se un giorno qualcuno contesta, si sa parola per
 * parola a cosa ha detto si'.
 */
export const TESTI_CONSENSO = {
  privacy: "Dichiaro di aver letto e capito l'informativa sulla privacy",
  informazioni: 'Voglio essere informato da SLAB su prodotti, servizi, eventi su tematiche affini a quelle dei corsi',
  // SLAB, a fine corso o evento, passa i nominativi alle aziende che vi hanno
  // partecipato, che li usano per il loro marketing. Serve un consenso a
  // parte, libero: chi dice No si iscrive lo stesso (GDPR art. 7).
  partner:
    'Acconsento che SLAB comunichi i miei dati di contatto alle aziende che partecipano ai suoi corsi ed eventi, perché possano inviarmi informazioni su prodotti, servizi ed eventi (finalità di marketing)',
  profilazione:
    'Voglio rendere semplice e immediata la mia esperienza di navigazione e ricevere suggerimenti calibrati sui miei gusti e le mie preferenze',
} as const;

/** Nome del modulo come compare nella scheda del lead. */
export const NOME_MODULO = 'Iscrizione corsi';

// ---------------------------------------------------------------------------
// Forma dei dati
// ---------------------------------------------------------------------------

export type SiNo = 'si' | 'no';

/** Il modulo cosi' come lo compila la persona: tutto testo, tutto ancora da controllare. */
export interface ModuloIscrizione {
  email: string;
  ripetiEmail: string;
  nome: string;
  cognome: string;
  dataNascita: string;
  luogoNascita: string;
  codiceFiscale: string;
  titolo: string;
  azienda: string;
  cellulare: string;
  qualifica: string;
  attivita: string;
  dipendenti: string;
  nazione: string;
  regione: string;
  provincia: string;
  citta: string;
  cap: string;
  indirizzo: string;
  cfp: SiNo | '';
  /** I tre campi dell'ordine servono solo a chi chiede i crediti CFP. */
  ordine: string;
  provinciaOrdine: string;
  numeroIscrizione: string;
  ore81: SiNo | '';
  privacy: boolean;
  consensoInformazioni: SiNo | '';
  consensoPartner: SiNo | '';
  consensoProfilazione: SiNo | '';
}

export type CampoModulo = keyof ModuloIscrizione;

export const MODULO_VUOTO: ModuloIscrizione = {
  email: '', ripetiEmail: '', nome: '', cognome: '', dataNascita: '', luogoNascita: '', codiceFiscale: '',
  titolo: '', azienda: '', cellulare: '', qualifica: '', attivita: '', dipendenti: '',
  nazione: NAZIONE_ITALIA, regione: '', provincia: '', citta: '', cap: '', indirizzo: '',
  cfp: '', ordine: '', provinciaOrdine: '', numeroIscrizione: '', ore81: '', privacy: false,
  consensoInformazioni: '', consensoPartner: '', consensoProfilazione: '',
};

/** Il modulo dopo i controlli: valori ripuliti e pronti da salvare. */
export interface Iscrizione {
  email: string;
  nome: string;
  cognome: string;
  /** yyyy-mm-dd */
  dataNascita: string;
  luogoNascita: string;
  /** Vuoto solo per chi non risiede in Italia e non l'ha indicato. */
  codiceFiscale: string;
  titolo: string;
  azienda: string;
  cellulare: string;
  qualifica: string;
  attivita: string;
  dipendenti: string;
  nazione: string;
  /** Vuoti fuori dall'Italia. */
  regione: string;
  provinciaSigla: string;
  provinciaNome: string;
  citta: string;
  cap: string;
  indirizzo: string;
  cfp: boolean;
  /** Valorizzati solo se ha chiesto i crediti CFP. */
  ordine: string | null;
  provinciaOrdine: string | null;
  numeroIscrizione: string | null;
  ore81: boolean;
  consensoInformazioni: boolean;
  consensoPartner: boolean;
  consensoProfilazione: boolean;
  /** Il corso indicato nel link del modulo, se c'era. */
  corso: string | null;
}

export type ErroriModulo = Partial<Record<CampoModulo, string>>;

export type EsitoValidazione = { ok: true; iscrizione: Iscrizione } | { ok: false; errori: ErroriModulo };

// ---------------------------------------------------------------------------
// Controlli
// ---------------------------------------------------------------------------

const LUNGHEZZA_MASSIMA = 120;
const LUNGHEZZA_MASSIMA_INDIRIZZO = 200;
const LUNGHEZZA_MASSIMA_CORSO = 150;
const ETA_MINIMA = 14;
const ETA_MASSIMA = 100;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SIGLE_PROVINCE = new Set(REGIONI.flatMap((r) => r.province.map((pr) => pr.sigla)));
const OBBLIGATORIO = 'Campo obbligatorio';

/** Testo ripulito da spazi doppi e caratteri invisibili; vuoto se non e' testo. */
function testo(valore: unknown, massimo = LUNGHEZZA_MASSIMA): string {
  if (typeof valore !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return valore.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, massimo);
}

/**
 * "mario ROSSI" -> "Mario Rossi", ma solo se chi scrive ha usato tutto
 * minuscolo o tutto maiuscolo: un "De Luca" o un "McArthur" scritti a modo
 * restano come sono.
 */
export function maiuscoleIniziali(valore: string): string {
  if (valore !== valore.toLowerCase() && valore !== valore.toUpperCase()) return valore;
  return valore.toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (_, prima: string, lettera: string) => prima + lettera.toUpperCase());
}

function siNo(valore: unknown): SiNo | '' {
  return valore === 'si' || valore === 'no' ? valore : '';
}

function tra<T extends string>(valore: string, ammessi: readonly T[]): valore is T {
  return (ammessi as readonly string[]).includes(valore);
}

/** Anni compiuti alla data di oggi (entrambe yyyy-mm-dd). */
export function etaAl(dataNascita: string, oggi: string): number {
  const [an, mn, gn] = dataNascita.split('-').map(Number);
  const [ao, mo, go] = oggi.split('-').map(Number);
  const compleannoPassato = mo > mn || (mo === mn && go >= gn);
  return ao - an - (compleannoPassato ? 0 : 1);
}

function dataEsistente(valore: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valore)) return false;
  const [a, m, g] = valore.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, g));
  return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === g;
}

function cifreTelefono(valore: string): number {
  return valore.replace(/\D/g, '').length;
}

/**
 * Controlla il modulo e, se va bene, lo restituisce ripulito.
 *
 * `oggi` (yyyy-mm-dd, ora italiana) serve per l'eta': passarlo da fuori
 * rende il controllo ripetibile nei test.
 */
export function validaIscrizione(input: unknown, oggi: string, corso?: unknown): EsitoValidazione {
  const dati = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  const errori: ErroriModulo = {};

  const email = testo(dati.email, 254).toLowerCase();
  const ripetiEmail = testo(dati.ripetiEmail, 254).toLowerCase();
  if (!email) errori.email = OBBLIGATORIO;
  else if (!EMAIL.test(email)) errori.email = 'Indirizzo email non valido';
  if (!ripetiEmail) errori.ripetiEmail = OBBLIGATORIO;
  else if (email && ripetiEmail !== email) errori.ripetiEmail = 'Le due email non coincidono';

  const nome = maiuscoleIniziali(testo(dati.nome));
  const cognome = maiuscoleIniziali(testo(dati.cognome));
  if (!nome) errori.nome = OBBLIGATORIO;
  if (!cognome) errori.cognome = OBBLIGATORIO;

  const dataNascita = testo(dati.dataNascita, 10);
  if (!dataNascita) errori.dataNascita = OBBLIGATORIO;
  else if (!dataEsistente(dataNascita)) errori.dataNascita = 'Data non valida';
  else {
    const eta = etaAl(dataNascita, oggi);
    if (eta < ETA_MINIMA || eta > ETA_MASSIMA) errori.dataNascita = 'Controlla la data di nascita';
  }

  const luogoNascita = maiuscoleIniziali(testo(dati.luogoNascita));
  if (!luogoNascita) errori.luogoNascita = OBBLIGATORIO;

  const nazione = testo(dati.nazione);
  if (!nazione) errori.nazione = OBBLIGATORIO;
  else if (!tra(nazione, NAZIONI)) errori.nazione = 'Scegli una voce dall’elenco';
  const inItalia = nazione === NAZIONE_ITALIA;

  // Il codice fiscale serve per l'attestato. Chi vive all'estero puo' non
  // averlo: per loro e' facoltativo, ma se lo scrivono deve essere giusto.
  const codiceFiscale = normalizzaCodiceFiscale(testo(dati.codiceFiscale, 20));
  if (!codiceFiscale) {
    if (inItalia) errori.codiceFiscale = OBBLIGATORIO;
  } else if (!codiceFiscaleValido(codiceFiscale)) {
    errori.codiceFiscale = 'Codice fiscale non valido: controlla i caratteri';
  } else if (!errori.dataNascita && dataNascita && !dataNascitaCoincide(codiceFiscale, dataNascita)) {
    errori.codiceFiscale = 'Il codice fiscale non corrisponde alla data di nascita';
  }

  const titolo = testo(dati.titolo);
  if (!titolo) errori.titolo = OBBLIGATORIO;
  else if (!tra(titolo, TITOLI)) errori.titolo = 'Scegli una voce dall’elenco';

  const azienda = testo(dati.azienda);
  if (!azienda) errori.azienda = OBBLIGATORIO;

  const cellulare = testo(dati.cellulare, 30);
  if (!cellulare) errori.cellulare = OBBLIGATORIO;
  else if (!/^[\d\s+().-]+$/.test(cellulare) || cifreTelefono(cellulare) < 6 || cifreTelefono(cellulare) > 15) {
    errori.cellulare = 'Numero non valido';
  }

  const qualifica = testo(dati.qualifica);
  if (!qualifica) errori.qualifica = OBBLIGATORIO;
  else if (!tra(qualifica, QUALIFICHE)) errori.qualifica = 'Scegli una voce dall’elenco';

  const attivita = testo(dati.attivita);
  if (!attivita) errori.attivita = OBBLIGATORIO;
  else if (!tra(attivita, ATTIVITA)) errori.attivita = 'Scegli una voce dall’elenco';

  const dipendenti = testo(dati.dipendenti);
  if (!dipendenti) errori.dipendenti = OBBLIGATORIO;
  else if (!tra(dipendenti, DIPENDENTI)) errori.dipendenti = 'Scegli una voce dall’elenco';

  // Regione e provincia hanno senso solo in Italia.
  const regione = inItalia ? testo(dati.regione) : '';
  const siglaScelta = inItalia ? testo(dati.provincia, 2).toUpperCase() : '';
  const provincia = inItalia ? trovaProvincia(regione, siglaScelta) : undefined;
  if (inItalia) {
    if (!regione) errori.regione = OBBLIGATORIO;
    if (!siglaScelta) errori.provincia = OBBLIGATORIO;
    else if (!provincia) errori.provincia = 'La provincia non appartiene alla regione scelta';
  }

  const citta = maiuscoleIniziali(testo(dati.citta));
  if (!citta) errori.citta = OBBLIGATORIO;

  const cap = testo(dati.cap, 10);
  if (!cap) errori.cap = OBBLIGATORIO;
  else if (inItalia && !/^\d{5}$/.test(cap)) errori.cap = 'Il CAP è di 5 cifre';

  const indirizzo = testo(dati.indirizzo, LUNGHEZZA_MASSIMA_INDIRIZZO);
  if (!indirizzo) errori.indirizzo = OBBLIGATORIO;

  const cfp = siNo(dati.cfp);
  const ore81 = siNo(dati.ore81);
  if (!cfp) errori.cfp = 'Scegli Sì o No';
  if (!ore81) errori.ore81 = 'Scegli Sì o No';

  // Per i crediti l'ente deve sapere a quale ordine comunicarli.
  const conCfp = cfp === 'si';
  const ordine = conCfp ? testo(dati.ordine) : '';
  const provinciaOrdine = conCfp ? testo(dati.provinciaOrdine, 2).toUpperCase() : '';
  const numeroIscrizione = conCfp ? testo(dati.numeroIscrizione, 20) : '';
  if (conCfp) {
    if (!ordine) errori.ordine = OBBLIGATORIO;
    else if (!tra(ordine, ORDINI_PROFESSIONALI)) errori.ordine = 'Scegli una voce dall’elenco';
    if (!provinciaOrdine) errori.provinciaOrdine = OBBLIGATORIO;
    else if (!SIGLE_PROVINCE.has(provinciaOrdine)) errori.provinciaOrdine = 'Scegli una voce dall’elenco';
    if (!numeroIscrizione) errori.numeroIscrizione = OBBLIGATORIO;
    else if (!/^[A-Za-z0-9/.\- ]+$/.test(numeroIscrizione)) errori.numeroIscrizione = 'Solo lettere, cifre, / e -';
  }

  if (dati.privacy !== true) errori.privacy = 'Per iscriverti devi dichiarare di aver letto l’informativa';

  const consensoInformazioni = siNo(dati.consensoInformazioni);
  const consensoPartner = siNo(dati.consensoPartner);
  const consensoProfilazione = siNo(dati.consensoProfilazione);
  if (!consensoInformazioni) errori.consensoInformazioni = 'Scegli Sì o No';
  if (!consensoPartner) errori.consensoPartner = 'Scegli Sì o No';
  if (!consensoProfilazione) errori.consensoProfilazione = 'Scegli Sì o No';

  if (Object.keys(errori).length > 0) return { ok: false, errori };

  return {
    ok: true,
    iscrizione: {
      email,
      nome,
      cognome,
      dataNascita,
      luogoNascita,
      codiceFiscale,
      titolo,
      azienda,
      cellulare,
      qualifica,
      attivita,
      dipendenti,
      nazione,
      regione,
      provinciaSigla: provincia?.sigla ?? '',
      provinciaNome: provincia?.nome ?? '',
      citta,
      cap,
      indirizzo,
      cfp: conCfp,
      ordine: conCfp ? ordine : null,
      provinciaOrdine: conCfp ? provinciaOrdine : null,
      numeroIscrizione: conCfp ? numeroIscrizione : null,
      ore81: ore81 === 'si',
      consensoInformazioni: consensoInformazioni === 'si',
      consensoPartner: consensoPartner === 'si',
      consensoProfilazione: consensoProfilazione === 'si',
      corso: testo(corso, LUNGHEZZA_MASSIMA_CORSO) || null,
    },
  };
}

// ---------------------------------------------------------------------------
// Composizione di lead e cliente
// ---------------------------------------------------------------------------

/** 1980-05-03 -> 03/05/1980 */
export function dataItaliana(iso: string): string {
  const [a, m, g] = iso.split('-');
  return `${g}/${m}/${a}`;
}

const siONo = (valore: boolean): string => (valore ? 'Sì' : 'No');

export function nomeCompleto(i: Iscrizione): string {
  return `${i.nome} ${i.cognome}`;
}

/**
 * Nome della scheda cliente. Le persone fisiche che arrivano da Fatture in
 * Cloud sono scritte COGNOME NOME in maiuscolo: i corsisti seguono la stessa
 * regola, cosi' nell'elenco clienti stanno in ordine con gli altri.
 */
export function ragioneSocialeCorsista(i: Iscrizione): string {
  return `${i.cognome} ${i.nome}`.toUpperCase();
}

/**
 * Il riepilogo leggibile che finisce nelle note di lead e cliente.
 * `intestazione` sostituisce la prima riga (es. la registrazione di un
 * biglietto di un evento invece del modulo corsi).
 */
export function riepilogoIscrizione(i: Iscrizione, intestazione?: string): string {
  const localita = [
    [i.cap, i.citta].filter(Boolean).join(' '),
    i.provinciaSigla ? `(${i.provinciaSigla})` : '',
  ].filter(Boolean).join(' ');

  const righe = [
    intestazione ?? (i.corso ? `Iscrizione dal modulo corsi — Corso: ${i.corso}` : 'Iscrizione dal modulo corsi'),
    `Nato/a a ${i.luogoNascita} il ${dataItaliana(i.dataNascita)}${i.codiceFiscale ? ` · CF ${i.codiceFiscale}` : ''}`,
    `Titolo: ${i.titolo} · Qualifica: ${i.qualifica}`,
    `Azienda/Ente: ${i.azienda} · Attività: ${i.attivita} · Dipendenti: ${i.dipendenti}`,
    `Indirizzo: ${[i.indirizzo, localita, i.regione, i.nazione].filter(Boolean).join(', ')}`,
    i.cfp
      ? `Crediti CFP: Sì — ${i.ordine} di ${i.provinciaOrdine}, iscrizione n. ${i.numeroIscrizione}`
      : 'Crediti CFP: No',
    `Ore aggiornamento 81/08: ${siONo(i.ore81)}`,
    `Consensi: comunicazioni SLAB ${siONo(i.consensoInformazioni)} · dati alle aziende partecipanti ${siONo(i.consensoPartner)} · profilazione ${siONo(i.consensoProfilazione)}`,
  ];
  return righe.join('\n');
}

/** La riga da inserire in `leads`. `raw` lo aggiunge chi salva. */
export function datiLead(i: Iscrizione) {
  return {
    source: 'sito' as const,
    stage: 'nuovo' as const,
    form_name: NOME_MODULO,
    campaign_name: i.corso,
    full_name: nomeCompleto(i),
    company_name: i.azienda,
    email: i.email,
    phone: i.cellulare,
    city: i.citta,
    province: i.provinciaSigla || null,
    notes: riepilogoIscrizione(i),
  };
}

/**
 * La scheda cliente di un corsista nuovo. Nasce "prospect", come ogni cliente
 * collegato a un lead ancora aperto: quando il lead passa a Vinto il
 * gestionale la promuove da solo a cliente attivo.
 *
 * `adesso` (data e ora ISO) e' il momento del consenso per le aziende
 * partecipanti, se l'ha dato.
 */
export function datiNuovoCliente(i: Iscrizione, adesso: string) {
  return {
    company_name: ragioneSocialeCorsista(i),
    contact_person: nomeCompleto(i),
    email: i.email,
    phone: i.cellulare,
    address: i.indirizzo,
    city: i.citta,
    postal_code: i.cap,
    province: i.provinciaSigla || null,
    country: i.nazione,
    tax_code: i.codiceFiscale || null,
    birth_date: i.dataNascita,
    birth_place: i.luogoNascita,
    is_corsista: true,
    partner_consent_at: i.consensoPartner ? adesso : null,
    status: 'prospect' as const,
    notes: riepilogoIscrizione(i),
  };
}
